import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSnackbar } from 'notistack';
import wsfeConfigService, {
  AfipConsultaComprobanteDetalle,
  TIPOS_WS,
  WsfeConfigDto,
  WsfeConfigItem,
} from '../../services/wsfeConfigService';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import SearchableSelect from '../../components/SearchableSelect';

interface Props {
  idCompania: string;
  /** CUIT del maestro de compañía (precarga la pestaña Consultas). */
  cuitCompania?: string;
}

const defaultForm: WsfeConfigDto = {
  idwsfe: '',
  idCompania: '',
  tipows: 'WSFEv1',
  descripcion: '',
  certificado: '',
  claveprivada: '',
  urlautenticacion: 'https://wsaahomo.afip.gov.ar/ws/services/LoginCms',
  urlwebservices: 'https://wswhomo.afip.gov.ar/wsfev1/service.asmx',
};

const URL_AUTENTICACION_OPTIONS = [
  {
    label: 'Homologacion',
    value: 'https://wsaahomo.afip.gov.ar/ws/services/LoginCms',
  },
  {
    label: 'Produccion',
    value: 'https://wsaa.afip.gov.ar/ws/services/LoginCms',
  },
] as const;

const URL_WEBSERVICES_BY_TIPO_WS: Record<string, readonly { label: string; value: string }[]> = {
  WSFEv1: [
    { label: 'Homologacion', value: 'https://wswhomo.afip.gov.ar/wsfev1/service.asmx' },
    { label: 'Produccion', value: 'https://servicios1.afip.gov.ar/wsfev1/service.asmx' },
  ],
  WSFEXv1: [
    { label: 'Homologacion', value: 'https://wswhomo.afip.gov.ar/wsfexv1/service.asmx' },
    { label: 'Produccion', value: 'https://servicios1.afip.gov.ar/wsfexv1/service.asmx' },
  ],
  WSMTXCA: [
    { label: 'Homologacion', value: 'https://wswhomo.afip.gov.ar/wsmtxca/services/MTXCAService' },
    { label: 'Produccion', value: 'https://serviciosjava.afip.gob.ar/wsmtxca/services/MTXCAService' },
  ],
  WSFECred: [
    { label: 'Homologacion', value: 'https://fwshomo.afip.gov.ar/wsfecred/FECredService?wsdl' },
    { label: 'Produccion', value: 'https://serviciosjava.afip.gob.ar/wsfecred/FECredService?wsdl' },
  ],
  WSBFEv1: [
    { label: 'Homologacion', value: 'https://wswhomo.afip.gov.ar/wsbfev1/service.asmx' },
    { label: 'Produccion', value: 'https://servicios1.afip.gov.ar/wsbfev1/service.asmx' },
  ],
  WSSrPadronA5: [
    { label: 'Homologacion', value: 'https://awshomo.afip.gov.ar/sr-padron/webservices/personaServiceA5' },
    { label: 'Produccion', value: 'https://aws.afip.gov.ar/sr-padron/webservices/personaServiceA5' },
  ],
};

function getWsUrlsByTipo(tipoWs?: string) {
  return URL_WEBSERVICES_BY_TIPO_WS[tipoWs ?? ''] ?? URL_WEBSERVICES_BY_TIPO_WS.WSFEv1;
}

function nombreCorto(ruta: string | undefined | null): string {
  if (!ruta) return '';
  const sep = ruta.includes('/') ? '/' : '\\';
  return ruta.split(sep).pop() ?? ruta;
}

const TIPOS_COMPROBANTE_AFIP = [
  { codigo: '1', label: '1 — Factura A' },
  { codigo: '6', label: '6 — Factura B' },
  { codigo: '11', label: '11 — Factura C' },
  { codigo: '3', label: '3 — Nota de crédito A' },
  { codigo: '8', label: '8 — Nota de crédito B' },
  { codigo: '13', label: '13 — Nota de crédito C' },
] as const;

function esTipoWsfev1(tipows?: string | null): boolean {
  return (tipows ?? '').trim().toUpperCase() === 'WSFEV1';
}

function docTipoAfipLabel(tipo?: number | null): string {
  switch (tipo) {
    case 80:
      return 'CUIT';
    case 86:
      return 'CUIL';
    case 87:
      return 'CDI';
    case 96:
      return 'DNI';
    case 99:
      return 'Consumidor final';
    default:
      return tipo != null ? `Doc. tipo ${tipo}` : '';
  }
}

function formatDocReceptor(tipo?: number | null, nro?: string | null): string {
  const limpio = (nro ?? '').replace(/\D/g, '');
  if (!limpio || limpio === '0') {
    return tipo === 99 ? 'Consumidor final (sin identificar)' : '—';
  }
  if (tipo === 80 && limpio.length === 11) {
    return `${limpio.slice(0, 2)}-${limpio.slice(2, 10)}-${limpio.slice(10)}`;
  }
  return nro?.trim() ?? limpio;
}

function condicionIvaAfipLabel(id?: number | null): string {
  switch (id) {
    case 1:
      return 'IVA Responsable Inscripto';
    case 4:
      return 'IVA Sujeto Exento';
    case 5:
      return 'Consumidor Final';
    case 6:
      return 'Responsable Monotributo';
    case 7:
      return 'Monotributista Social';
    case 8:
      return 'No Categorizado';
    case 9:
      return 'IVA Liberado';
    case 10:
      return 'IVA No Alcanzado';
    case 13:
      return 'Monotributo Trabajador Independiente Promovido';
    default:
      return id != null ? `Condición IVA ${id}` : '';
  }
}

function conceptoAfipLabel(concepto?: number | null): string {
  switch (concepto) {
    case 1:
      return 'Productos';
    case 2:
      return 'Servicios';
    case 3:
      return 'Productos y servicios';
    default:
      return concepto != null ? `Concepto ${concepto}` : '';
  }
}

function formatFechaAfip(fecha?: string | null): string {
  const f = (fecha ?? '').trim();
  if (f.length === 8 && /^\d+$/.test(f)) {
    return `${f.slice(6, 8)}/${f.slice(4, 6)}/${f.slice(0, 4)}`;
  }
  return f;
}

function formatImporteAfip(valor?: string | null): string {
  const v = (valor ?? '').trim();
  if (!v) return '';
  const n = Number(v.replace(',', '.'));
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ConsultaDetalleFilas({ detalle }: { detalle: AfipConsultaComprobanteDetalle }) {
  const docLabel = docTipoAfipLabel(detalle.docTipo);
  const docNro = formatDocReceptor(detalle.docTipo, detalle.docNro);
  const condIva = condicionIvaAfipLabel(detalle.condicionIvaReceptorId);
  const concepto = conceptoAfipLabel(detalle.concepto);
  const moneda =
    detalle.monId != null && detalle.monId !== ''
      ? detalle.monId === 'PES'
        ? 'Pesos ($)'
        : `${detalle.monId}${detalle.monCotiz ? ` (cotiz. ${detalle.monCotiz})` : ''}`
      : '';

  return (
    <Stack spacing={0.5} component="div">
      {detalle.puntoVenta != null && detalle.codigoComprobante != null && detalle.numeroComprobante != null && (
        <Typography variant="body2">
          Comprobante: PtoVta {detalle.puntoVenta} — Tipo {detalle.codigoComprobante} — Nro.{' '}
          {detalle.numeroComprobante}
        </Typography>
      )}
      {detalle.resultado != null && detalle.resultado !== '' && (
        <Typography variant="body2">
          Resultado AFIP: {detalle.resultado}
          {detalle.resultado.toUpperCase() === 'A'
            ? ' (Aprobado)'
            : detalle.resultado.toUpperCase() === 'R'
              ? ' (Rechazado)'
              : ''}
        </Typography>
      )}
      {(docLabel || docNro !== '—') && (
        <Typography variant="body2">
          Receptor: {docLabel ? `${docLabel}: ` : ''}
          {docNro}
        </Typography>
      )}
      {condIva !== '' && <Typography variant="body2">Condición IVA receptor: {condIva}</Typography>}
      {concepto !== '' && <Typography variant="body2">Concepto: {concepto}</Typography>}
      {detalle.cae != null && detalle.cae !== '' && (
        <Typography variant="body2">CAE: {detalle.cae}</Typography>
      )}
      {detalle.fechaComprobante != null && detalle.fechaComprobante !== '' && (
        <Typography variant="body2">Fecha comprobante: {formatFechaAfip(detalle.fechaComprobante)}</Typography>
      )}
      {detalle.fechaVencimientoCae != null && detalle.fechaVencimientoCae !== '' && (
        <Typography variant="body2">Venc. CAE: {formatFechaAfip(detalle.fechaVencimientoCae)}</Typography>
      )}
      {detalle.fchProceso != null && detalle.fchProceso !== '' && (
        <Typography variant="body2">Fecha proceso AFIP: {formatFechaAfip(detalle.fchProceso)}</Typography>
      )}
      {(detalle.fchServDesde != null && detalle.fchServDesde !== '') ||
      (detalle.fchServHasta != null && detalle.fchServHasta !== '') ? (
        <Typography variant="body2">
          Período servicio: {formatFechaAfip(detalle.fchServDesde) || '—'} —{' '}
          {formatFechaAfip(detalle.fchServHasta) || '—'}
        </Typography>
      ) : null}
      {detalle.fchVtoPago != null && detalle.fchVtoPago !== '' && (
        <Typography variant="body2">Venc. pago: {formatFechaAfip(detalle.fchVtoPago)}</Typography>
      )}
      {moneda !== '' && <Typography variant="body2">Moneda: {moneda}</Typography>}
      {detalle.impNeto != null && detalle.impNeto !== '' && (
        <Typography variant="body2">Importe neto gravado: ${formatImporteAfip(detalle.impNeto)}</Typography>
      )}
      {detalle.impIva != null && detalle.impIva !== '' && (
        <Typography variant="body2">IVA: ${formatImporteAfip(detalle.impIva)}</Typography>
      )}
      {detalle.impOpEx != null && detalle.impOpEx !== '' && Number(detalle.impOpEx.replace(',', '.')) !== 0 && (
        <Typography variant="body2">Importe exento: ${formatImporteAfip(detalle.impOpEx)}</Typography>
      )}
      {detalle.impTrib != null && detalle.impTrib !== '' && Number(detalle.impTrib.replace(',', '.')) !== 0 && (
        <Typography variant="body2">Tributos: ${formatImporteAfip(detalle.impTrib)}</Typography>
      )}
      {detalle.importeTotal != null && detalle.importeTotal !== '' && (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Importe total: ${formatImporteAfip(detalle.importeTotal)}
        </Typography>
      )}
      {(detalle.cbteDesde != null || detalle.cbteHasta != null) && (
        <Typography variant="body2">
          Rango comprobantes: {detalle.cbteDesde ?? '—'} — {detalle.cbteHasta ?? '—'}
        </Typography>
      )}
      {detalle.emisionTipo != null && detalle.emisionTipo !== '' && (
        <Typography variant="body2">Tipo emisión: {detalle.emisionTipo}</Typography>
      )}
      {!detalle.exitoso && detalle.observaciones != null && detalle.observaciones !== '' && (
        <Typography variant="body2">Observaciones: {detalle.observaciones}</Typography>
      )}
    </Stack>
  );
}

export const CompaniaArcaPanel: React.FC<Props> = ({ idCompania, cuitCompania = '' }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [configs, setConfigs] = useState<WsfeConfigItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState<WsfeConfigDto>({ ...defaultForm, idCompania });
  const [saving, setSaving] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ exitoso: boolean; mensaje: string } | null>(null);
  const [selectedIdwsfe, setSelectedIdwsfe] = useState<string | null>(null);

  const [consultaCuit, setConsultaCuit] = useState('');
  const [consultaTipoSelect, setConsultaTipoSelect] = useState('1');
  const [consultaTipoOtro, setConsultaTipoOtro] = useState('');
  const [consultaPtoVta, setConsultaPtoVta] = useState('1');
  const [consultaNroCbte, setConsultaNroCbte] = useState('0');
  const [consultaUltimoLoading, setConsultaUltimoLoading] = useState(false);
  const [consultaCbteLoading, setConsultaCbteLoading] = useState(false);
  const [consultaCombinedLoading, setConsultaCombinedLoading] = useState(false);
  const [consultaUltimoInfo, setConsultaUltimoInfo] = useState<number | null>(null);
  const [consultaDetalle, setConsultaDetalle] = useState<AfipConsultaComprobanteDetalle | null>(null);

  const certInputRef = useRef<HTMLInputElement>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);

  const cargarConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const items = await wsfeConfigService.getWsfeConfigs(idCompania);
      setConfigs(items);
    } catch {
      enqueueSnackbar('Error al cargar configuraciones ARCA', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [idCompania, enqueueSnackbar]);

  useEffect(() => {
    cargarConfigs();
  }, [cargarConfigs]);

  const esWsfev1 = esTipoWsfev1(form.tipows);

  const resetConsultaState = () => {
    setConsultaDetalle(null);
    setConsultaUltimoInfo(null);
    setConsultaCuit(cuitCompania.trim());
    setConsultaTipoSelect('1');
    setConsultaTipoOtro('');
    setConsultaPtoVta('1');
    setConsultaNroCbte('0');
  };

  const getCodigoComprobanteAfip = (): number | null => {
    if (consultaTipoSelect === 'otro') {
      const n = parseInt(consultaTipoOtro, 10);
      return Number.isNaN(n) || n < 1 ? null : n;
    }
    const n = parseInt(consultaTipoSelect, 10);
    return Number.isNaN(n) ? null : n;
  };

  const validarParamsConsultaBase = (): { cuit: string; pv: number; tc: number } | null => {
    const cuit = consultaCuit.trim();
    if (!cuit) {
      enqueueSnackbar('El CUIT es requerido', { variant: 'warning' });
      return null;
    }
    const pv = parseInt(consultaPtoVta, 10);
    const tc = getCodigoComprobanteAfip();
    if (Number.isNaN(pv) || pv < 1) {
      enqueueSnackbar('Ingrese un punto de venta AFIP válido', { variant: 'warning' });
      return null;
    }
    if (tc == null) {
      enqueueSnackbar('Seleccione o ingrese un tipo de comprobante AFIP válido', { variant: 'warning' });
      return null;
    }
    return { cuit, pv, tc };
  };

  useEffect(() => {
    if (dialogOpen && !esWsfev1 && dialogTab === 2) {
      setDialogTab(0);
    }
  }, [dialogOpen, esWsfev1, dialogTab]);

  const abrirNuevo = () => {
    setForm({ ...defaultForm, idCompania });
    setEditando(false);
    setDialogTab(0);
    setTestResult(null);
    resetConsultaState();
    setDialogOpen(true);
  };

  const abrirEditar = async (idwsfe: string) => {
    try {
      const dto = await wsfeConfigService.getWsfeConfig(idwsfe);
      const wsOptions = getWsUrlsByTipo(dto.tipows);
      setForm({
        ...dto,
        urlautenticacion: URL_AUTENTICACION_OPTIONS.some(o => o.value === dto.urlautenticacion)
          ? dto.urlautenticacion
          : URL_AUTENTICACION_OPTIONS[0].value,
        urlwebservices: wsOptions.some(o => o.value === dto.urlwebservices)
          ? dto.urlwebservices
          : wsOptions[0].value,
      });
      setEditando(true);
      setDialogTab(0);
      setTestResult(null);
      resetConsultaState();
      setDialogOpen(true);
    } catch {
      enqueueSnackbar('Error al cargar el registro', { variant: 'error' });
    }
  };

  const cerrarDialog = () => {
    setDialogOpen(false);
    setTestResult(null);
    setConsultaDetalle(null);
    setConsultaUltimoInfo(null);
  };

  const handleTipoWsChange = (tipoWs: string) => {
    const wsOptions = getWsUrlsByTipo(tipoWs);
    setForm(prev => ({
      ...prev,
      tipows: tipoWs,
      urlautenticacion: URL_AUTENTICACION_OPTIONS.some(o => o.value === prev.urlautenticacion)
        ? prev.urlautenticacion
        : URL_AUTENTICACION_OPTIONS[0].value,
      urlwebservices: wsOptions.some(o => o.value === prev.urlwebservices) ? prev.urlwebservices : wsOptions[0].value,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleGuardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.idwsfe?.trim()) {
      enqueueSnackbar('El Id WSFE es requerido', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (editando) {
        await wsfeConfigService.updateWsfeConfig(form.idwsfe!, form);
      } else {
        await wsfeConfigService.createWsfeConfig({ ...form, idCompania });
      }
      enqueueSnackbar('Configuración guardada correctamente', { variant: 'success' });
      cerrarDialog();
      cargarConfigs();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async (idwsfe: string) => {
    if (!window.confirm(`¿Eliminar la configuración "${idwsfe}"?`)) return;
    try {
      await wsfeConfigService.deleteWsfeConfig(idwsfe);
      enqueueSnackbar('Configuración eliminada', { variant: 'success' });
      cargarConfigs();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al eliminar', { variant: 'error' });
    }
  };

  const handleUploadCert = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !form.idwsfe?.trim()) return;
    try {
      setUploadingCert(true);
      const result = await wsfeConfigService.uploadCertificado(form.idwsfe!, idCompania, file);
      setForm(prev => ({ ...prev, certificado: result.ruta }));
      enqueueSnackbar('Certificado subido correctamente', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al subir certificado', { variant: 'error' });
    } finally {
      setUploadingCert(false);
      if (certInputRef.current) certInputRef.current.value = '';
    }
  };

  const handleUploadKey = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !form.idwsfe?.trim()) return;
    try {
      setUploadingKey(true);
      const result = await wsfeConfigService.uploadClavePrivada(form.idwsfe!, idCompania, file);
      setForm(prev => ({ ...prev, claveprivada: result.ruta }));
      enqueueSnackbar('Clave privada subida correctamente', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al subir clave privada', { variant: 'error' });
    } finally {
      setUploadingKey(false);
      if (keyInputRef.current) keyInputRef.current.value = '';
    }
  };

  const copiarAlPortapapeles = async (texto: string, mensajeExito: string) => {
    try {
      await navigator.clipboard.writeText(texto);
      enqueueSnackbar(mensajeExito, { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar al portapapeles', { variant: 'error' });
    }
  };

  const handleConsultaUltimoAutorizado = async () => {
    if (!form.idwsfe?.trim() || !esWsfev1) return;
    const params = validarParamsConsultaBase();
    if (!params) return;
    setConsultaUltimoLoading(true);
    setConsultaDetalle(null);
    setConsultaUltimoInfo(null);
    try {
      const r = await wsfeConfigService.consultaUltimoAutorizado(form.idwsfe.trim(), {
        idCompania,
        cuit: params.cuit,
        puntoVenta: params.pv,
        codigoComprobanteAfip: params.tc,
      });
      const n = r.ultimoNumero;
      if (n != null && n > 0) {
        setConsultaNroCbte(String(n));
        setConsultaUltimoInfo(n);
        enqueueSnackbar(`Último comprobante autorizado: ${n}`, { variant: 'success' });
      } else {
        setConsultaUltimoInfo(null);
        enqueueSnackbar('No hay comprobantes autorizados para punto de venta, tipo y CUIT indicados.', {
          variant: 'info',
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al consultar último comprobante', { variant: 'error' });
    } finally {
      setConsultaUltimoLoading(false);
    }
  };

  const handleConsultaComprobanteAutorizado = async () => {
    if (!form.idwsfe?.trim() || !esWsfev1) return;
    const params = validarParamsConsultaBase();
    if (!params) return;
    const nro = parseInt(consultaNroCbte, 10);
    if (Number.isNaN(nro) || nro < 1) {
      enqueueSnackbar('Ingrese un número de comprobante AFIP válido', { variant: 'warning' });
      return;
    }
    setConsultaCbteLoading(true);
    setConsultaDetalle(null);
    setConsultaUltimoInfo(null);
    try {
      const detalle = await wsfeConfigService.consultaComprobanteAutorizado(form.idwsfe.trim(), {
        idCompania,
        cuit: params.cuit,
        puntoVenta: params.pv,
        codigoComprobanteAfip: params.tc,
        numeroComprobante: nro,
      });
      setConsultaDetalle(detalle);
      if (detalle.exitoso) {
        enqueueSnackbar('Consulta realizada correctamente', { variant: 'success' });
      } else {
        enqueueSnackbar(detalle.mensaje || 'AFIP no devolvió datos del comprobante', { variant: 'warning' });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al consultar comprobante', { variant: 'error' });
    } finally {
      setConsultaCbteLoading(false);
    }
  };

  const handleConsultaUltimoConDetalle = async () => {
    if (!form.idwsfe?.trim() || !esWsfev1) return;
    const params = validarParamsConsultaBase();
    if (!params) return;
    setConsultaCombinedLoading(true);
    setConsultaDetalle(null);
    setConsultaUltimoInfo(null);
    try {
      const r = await wsfeConfigService.consultaUltimoAutorizado(form.idwsfe.trim(), {
        idCompania,
        cuit: params.cuit,
        puntoVenta: params.pv,
        codigoComprobanteAfip: params.tc,
      });
      const n = r.ultimoNumero;
      if (n == null || n < 1) {
        enqueueSnackbar('No hay comprobantes autorizados para punto de venta, tipo y CUIT indicados.', {
          variant: 'info',
        });
        return;
      }
      setConsultaNroCbte(String(n));
      const detalle = await wsfeConfigService.consultaComprobanteAutorizado(form.idwsfe.trim(), {
        idCompania,
        cuit: params.cuit,
        puntoVenta: params.pv,
        codigoComprobanteAfip: params.tc,
        numeroComprobante: n,
      });
      setConsultaDetalle(detalle);
      if (detalle.exitoso) {
        enqueueSnackbar(`Detalle del comprobante ${n} obtenido correctamente`, { variant: 'success' });
      } else {
        enqueueSnackbar(detalle.mensaje || 'AFIP no devolvió datos del comprobante', { variant: 'warning' });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Error al consultar último comprobante con detalle', {
        variant: 'error',
      });
    } finally {
      setConsultaCombinedLoading(false);
    }
  };

  const handleProbarAcceso = async () => {
    if (!form.idwsfe?.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await wsfeConfigService.probarAcceso(form.idwsfe!);
      setTestResult(result);
    } catch (err: any) {
      setTestResult({
        exitoso: false,
        mensaje: err?.response?.data?.message || 'Error al probar la conexión',
      });
    } finally {
      setTestLoading(false);
    }
  };

  const wsOptions = getWsUrlsByTipo(form.tipows);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={abrirNuevo}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Nuevo Certificado
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Id WSFE</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo WS</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Certificado</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Clave Privada</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 96 }} align="center">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    No hay configuraciones. Use "Agregar" para crear una.
                  </TableCell>
                </TableRow>
              ) : (
                configs.map(c => (
                  <TableRow
                    key={c.idwsfe}
                    hover
                    selected={selectedIdwsfe === c.idwsfe}
                    onClick={() => setSelectedIdwsfe(prev => (prev === c.idwsfe ? null : c.idwsfe))}
                    onDoubleClick={() => abrirEditar(c.idwsfe)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{c.idwsfe}</TableCell>
                    <TableCell>{c.tipows}</TableCell>
                    <TableCell>{c.descripcion}</TableCell>
                    <TableCell>
                      <Tooltip title={c.certificado ?? ''}>
                        <span>{nombreCorto(c.certificado) || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={c.claveprivada ?? ''}>
                        <span>{nombreCorto(c.claveprivada) || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Modificar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              abrirEditar(c.idwsfe);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              handleEliminar(c.idwsfe);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={cerrarDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: '#1976d2', pb: 0 }}>
          {editando ? 'Modificar registro ARCA' : 'Agregar registro ARCA'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={dialogTab}
              onChange={(_, v) => setDialogTab(v)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                '& .MuiTabs-indicator': { backgroundColor: '#1976d2' },
              }}
            >
              <Tab label="Datos del ticket de acceso" />
              <Tab label="Test conexión WS" disabled={!editando} />
              <Tab label="Consultas" disabled={!editando || !esWsfev1} />
            </Tabs>
          </Box>

          {/* Pestaña 0: Datos del ticket */}
          {dialogTab === 0 && (
            <Box component="form" id="wsfe-form" onSubmit={handleGuardar}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Id WSFE"
                    name="idwsfe"
                    value={form.idwsfe ?? ''}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={editando}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Descripción"
                    name="descripcion"
                    value={form.descripcion ?? ''}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SearchableSelect
                    label="Tipo WS"
                    value={form.tipows ?? 'WSFEv1'}
                    onChange={(val) => handleTipoWsChange(val ?? 'WSFEv1')}
                    options={TIPOS_WS.map((t) => ({ value: t, label: t }))}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={8} />

                {/* Certificado */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Certificado (.crt / .pem / .cer)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      value={nombreCorto(form.certificado) ?? ''}
                      fullWidth
                      size="small"
                      placeholder="Sin archivo seleccionado"
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      disabled={uploadingCert || !form.idwsfe?.trim()}
                      sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                      {uploadingCert ? <CircularProgress size={16} /> : 'Examinar…'}
                      <input
                        ref={certInputRef}
                        type="file"
                        accept=".crt,.pem,.cer"
                        hidden
                        onChange={handleUploadCert}
                      />
                    </Button>
                  </Stack>
                </Grid>

                {/* Clave privada */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Clave Privada (.key / .pem)
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      value={nombreCorto(form.claveprivada) ?? ''}
                      fullWidth
                      size="small"
                      placeholder="Sin archivo seleccionado"
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      disabled={uploadingKey || !form.idwsfe?.trim()}
                      sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                      {uploadingKey ? <CircularProgress size={16} /> : 'Examinar…'}
                      <input
                        ref={keyInputRef}
                        type="file"
                        accept=".key,.pem"
                        hidden
                        onChange={handleUploadKey}
                      />
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SearchableSelect
                      label="URL Autenticacion"
                      value={form.urlautenticacion ?? URL_AUTENTICACION_OPTIONS[0].value}
                      onChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          urlautenticacion: val ?? URL_AUTENTICACION_OPTIONS[0].value,
                        }))
                      }
                      options={URL_AUTENTICACION_OPTIONS.map((option) => ({
                        value: option.value,
                        label: `${option.label} - ${option.value}`,
                      }))}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Tooltip title="Copiar URL de autenticación">
                      <IconButton
                        size="small"
                        color="primary"
                        edge="end"
                        onClick={() =>
                          void copiarAlPortapapeles(
                            form.urlautenticacion ?? URL_AUTENTICACION_OPTIONS[0].value,
                            'URL de autenticación copiada'
                          )
                        }
                        sx={{ flexShrink: 0 }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SearchableSelect
                      label="URL Web Services"
                      value={form.urlwebservices ?? wsOptions[0].value}
                      onChange={(val) =>
                        setForm((prev) => ({
                          ...prev,
                          urlwebservices: val ?? wsOptions[0].value,
                        }))
                      }
                      options={wsOptions.map((option) => ({
                        value: option.value,
                        label: `${option.label} - ${option.value}`,
                      }))}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Tooltip title="Copiar URL de web services">
                      <IconButton
                        size="small"
                        color="primary"
                        edge="end"
                        onClick={() =>
                          void copiarAlPortapapeles(
                            form.urlwebservices ?? wsOptions[0].value,
                            'URL de web services copiada'
                          )
                        }
                        sx={{ flexShrink: 0 }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  sx={{ textTransform: 'none', borderRadius: 2, px: 3 }}
                >
                  {saving ? <CircularProgress size={20} /> : 'Aceptar'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={cerrarDialog}
                  disabled={saving}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Cancelar
                </Button>
              </Stack>
            </Box>
          )}

          {/* Pestaña 1: Test conexión */}
          {dialogTab === 1 && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Prueba la autenticación con ARCA/AFIP usando el certificado y clave privada configurados para{' '}
                <strong>{form.idwsfe}</strong>. La prueba obtiene un ticket de acceso real sin generar ningún
                comprobante.
              </Typography>
              <Button
                variant="contained"
                onClick={handleProbarAcceso}
                disabled={testLoading}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {testLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Probar conexión
              </Button>

              {testResult && (
                <Alert
                  severity={testResult.exitoso ? 'success' : 'error'}
                  icon={testResult.exitoso ? <CheckCircleIcon /> : <ErrorIcon />}
                  sx={{ mt: 2, borderRadius: 2 }}
                >
                  {testResult.mensaje}
                </Alert>
              )}
            </Box>
          )}

          {/* Pestaña 2: Consultas (WSFEv1 — mismo criterio que el sistema desktop) */}
          {dialogTab === 2 && (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Consulta de comprobantes autorizados
              </Typography>
              {!esWsfev1 ? (
                <Typography color="text.secondary">
                  Seleccione Tipo WS &quot;WSFEv1&quot; en la primera pestaña para usar estas consultas.
                </Typography>
              ) : (
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={7}>
                    <Stack spacing={2}>
                      <TextField
                        label="C.U.I.T."
                        value={consultaCuit}
                        onChange={e => setConsultaCuit(e.target.value)}
                        fullWidth
                        size="small"
                        required
                        placeholder="20-12345678-9"
                        helperText="CUIT del emisor registrado en AFIP"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <SearchableSelect
                        label="Tipo comprobante AFIP"
                        value={consultaTipoSelect || null}
                        onChange={(val) => setConsultaTipoSelect(val ?? '1')}
                        options={[
                          ...TIPOS_COMPROBANTE_AFIP.map((t) => ({
                            value: t.codigo,
                            label: t.label,
                          })),
                          { value: 'otro', label: 'Otro (ingresar código)' },
                        ]}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      {consultaTipoSelect === 'otro' && (
                        <TextField
                          label="Código AFIP"
                          value={consultaTipoOtro}
                          onChange={e => setConsultaTipoOtro(e.target.value)}
                          fullWidth
                          size="small"
                          type="number"
                          inputProps={{ min: 1 }}
                          helperText="Código numérico según tablas AFIP"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      )}
                      <TextField
                        label="Punto de venta"
                        value={consultaPtoVta}
                        onChange={e => setConsultaPtoVta(e.target.value)}
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 1 }}
                        helperText="Número de punto de venta AFIP (PtoVta)"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        label="Nro. comprobante AFIP"
                        value={consultaNroCbte}
                        onChange={e => setConsultaNroCbte(e.target.value)}
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ min: 0 }}
                        helperText="Se completa al consultar el último autorizado"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Stack spacing={2}>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={consultaCombinedLoading || consultaUltimoLoading || consultaCbteLoading}
                        onClick={() => void handleConsultaUltimoConDetalle()}
                        startIcon={
                          consultaCombinedLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon />
                          )
                        }
                        sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, justifyContent: 'flex-start' }}
                      >
                        Consultar último con detalle
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        disabled={consultaUltimoLoading || consultaCombinedLoading}
                        onClick={() => void handleConsultaUltimoAutorizado()}
                        startIcon={
                          consultaUltimoLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <HelpOutlineIcon />
                          )
                        }
                        sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, justifyContent: 'flex-start' }}
                      >
                        Último comprobante autorizado
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        size="large"
                        disabled={consultaCbteLoading || consultaCombinedLoading}
                        onClick={() => void handleConsultaComprobanteAutorizado()}
                        startIcon={
                          consultaCbteLoading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <HelpOutlineIcon />
                          )
                        }
                        sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, justifyContent: 'flex-start' }}
                      >
                        Consulta comprobante autorizado
                      </Button>
                    </Stack>
                  </Grid>
                  {consultaUltimoInfo != null && !consultaDetalle && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2, textAlign: 'left' }}>
                        Último comprobante autorizado en AFIP: <strong>{consultaUltimoInfo}</strong>. Use
                        &quot;Consulta comprobante autorizado&quot; o &quot;Consultar último con detalle&quot; para
                        ver CAE e importes.
                      </Alert>
                    </Grid>
                  )}
                  {consultaDetalle && (
                    <Grid item xs={12}>
                      <Alert
                        severity={consultaDetalle.exitoso ? 'success' : 'warning'}
                        sx={{ borderRadius: 2, textAlign: 'left' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          {consultaDetalle.mensaje}
                        </Typography>
                        <ConsultaDetalleFilas detalle={consultaDetalle} />
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompaniaArcaPanel;
